import React from 'react';
import { safeInvoke } from '@shared/utils/safeInvoke';
import { styles, StyledContainer, StyledDeleteContainer } from './styles';
import type { BoxProps } from '@withjoy/joykit';
export const TagVariant = {
  OUTLINED: 'outlined' as const,
  SOLID: 'solid' as const,
};

const Intent = {
  PRIMARY: 'primary' as const,
  SUCCESS: 'success' as const,
  DANGER: 'danger' as const,
  INFO: 'info' as const,
};

export type Intent = (typeof Intent)[keyof typeof Intent];

export type TagVariant = (typeof TagVariant)[keyof typeof TagVariant];
export type TagIntent = typeof Intent.PRIMARY;

export interface TagProps extends BoxProps<'span'> {
  children: React.ReactNode;
  a11yLabel?: string;
  disabled?: boolean;
  intent?: TagIntent;

  /**
   * @default 'solid'
   */
  variant?: TagVariant;

  onClick?: React.MouseEventHandler<HTMLSpanElement>;

  /**
   * Click handler for the remove button.
   */
  onRemove?: React.MouseEventHandler<HTMLSpanElement>;
}

const Tag: React.FC<TagProps> = React.memo(props => {
  const { a11yLabel, children, disabled, intent = Intent.PRIMARY, onClick, onRemove, variant = TagVariant.SOLID, ...restProps } = props;

  const onRemoveClickHandler: React.MouseEventHandler<HTMLElement> = e => {
    if (onRemove) {
      safeInvoke(onRemove, e, props);
    }
  };

  const isInteractive = !disabled && !!onClick;

  const htmlProps: Partial<React.HTMLAttributes<HTMLSpanElement>> = {
    'aria-label': a11yLabel,
  };

  if (isInteractive) {
    htmlProps.tabIndex = 0;
  }

  if (!disabled && onClick) {
    htmlProps.onClick = onClick;
  }

  return (
    <StyledContainer
      __css={styles.containerStyles({ variant, isInteractive, intent })}
      {...restProps}
      {...htmlProps}
      isInteractive={isInteractive}
      intent={intent}
      variant={variant}
      typographyVariant="hed1"
      color="currentColor"
    >
      {children}
      {onRemove && (
        <StyledDeleteContainer
          __css={styles.deleteContainerStyles}
          size={16}
          tabIndex={0}
          onClick={onRemoveClickHandler}
          title="Remove Filter"
        />
      )}
    </StyledContainer>
  );
});

Tag.displayName = 'Tag';

export default Tag;
